import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { eur, dateIt } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ModalityBadge from "@/components/app/ModalityBadge";
import ParticipantForm, { emptyParticipant, validateParticipant } from "@/components/app/ParticipantForm";
import ImportEnrollments from "@/components/app/ImportEnrollments";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, Link2 } from "lucide-react";
import { isGeneralCourse, isSpecificCourse } from "@/lib/specificTraining";

const needsEdition = (m) => m === "aula" || m === "videoconferenza";

function PriceBox({ c }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm space-y-1" data-testid="enroll-price-box">
      <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Prezzo personalizzato applicato</div>
      <div className="flex justify-between"><span>Imponibile</span><span className="font-semibold" data-testid="enroll-price">{eur(c.price)}</span></div>
      <div className="flex justify-between"><span>{Number(c.vat_rate) === 0 ? "IVA" : `IVA ${c.vat_rate}%`}</span><span data-testid="enroll-vat">{Number(c.vat_rate) === 0 ? "Esente IVA" : eur(c.vat_amount)}</span></div>
      <div className="flex justify-between font-bold border-t border-slate-200 pt-1"><span>Totale</span><span data-testid="enroll-total">{eur(c.total)}</span></div>
    </div>
  );
}

export default function ClienteNewEnrollment() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [courses, setCourses] = useState(null);
  const [me, setMe] = useState(null);
  const [courseId, setCourseId] = useState(params.get("corso") || "");
  const [editionId, setEditionId] = useState(params.get("edizione") || "");
  const [p, setP] = useState(emptyParticipant());
  const [specificId, setSpecificId] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/cliente/catalog").then((r) => setCourses(r.data));
    api.get("/cliente/me").then((r) => setMe(r.data));
  }, []);
  const course = useMemo(() => (courses || []).find((c) => c.course_id === courseId), [courses, courseId]);
  const specifics = useMemo(() => (course && isGeneralCourse(course.title) ? (courses || []).filter(isSpecificCourse) : []), [courses, course]);
  const isSlot = me?.billing_mode === "slot";
  const blocked = isSlot && (me?.slots_balance || 0) <= 0;
  const refreshMe = () => api.get("/cliente/me").then((r) => setMe(r.data));

  const submit = async () => {
    if (!course) return toast.error("Seleziona un corso");
    if (needsEdition(course.modality) && !editionId) return toast.error("Seleziona l'edizione del corso");
    const err = validateParticipant(p, course.cfp_profiles);
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const { data } = await api.post("/cliente/enrollments", { ...p, course_id: course.course_id, edition_id: editionId || null, specific_course_id: specificId || null });
      await refreshMe();
      setDone(data); toast.success(data.linked_enrollment ? "Iscrizioni confermate" : "Iscrizione confermata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reset = () => { setDone(null); setP(emptyParticipant()); setEditionId(""); setSpecificId(""); };

  if (!courses) return <div className="text-slate-500">Caricamento…</div>;
  if (done) return (
    <Card className="border-emerald-200 bg-emerald-50 max-w-xl" data-testid="enroll-success"><CardContent className="p-6 space-y-3">
      <div className="flex items-center gap-2 text-emerald-800 font-display text-2xl font-extrabold"><CheckCircle2 className="w-7 h-7" /> Iscrizione confermata</div>
      <div className="text-sm text-slate-700">{done.first_name} {done.last_name} · {done.course_title}</div>
      <div className="text-sm text-slate-700">Numero: <strong data-testid="enroll-success-number">{done.enrollment_number}</strong></div>
      {done.linked_enrollment && (
        <div className="text-sm text-slate-700" data-testid="enroll-success-linked">
          <Link2 className="w-4 h-4 inline mr-1" />{done.linked_enrollment.course_title} · n. <strong>{done.linked_enrollment.enrollment_number}</strong>
        </div>
      )}
      {me?.billing_mode === "slot" && <div className="text-sm text-slate-700">Slot residui: <strong>{me.slots_balance}</strong></div>}
      <div className="flex gap-2 pt-2">
        <Button onClick={reset} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="enroll-another">Nuova iscrizione</Button>
        <Button variant="outline" onClick={() => navigate("/cliente/storico")} data-testid="enroll-go-history">Storico iscrizioni</Button>
      </div>
    </CardContent></Card>
  );

  return (
    <div className="space-y-6 max-w-4xl" data-testid="cliente-new-enrollment">
      <div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Iscrizioni</h1>
        <p className="text-slate-500 mt-1">Seleziona il corso{isSlot ? "" : ", verifica il prezzo"} e compila i dati del corsista, oppure importa più iscrizioni da Excel.</p>
      </div>
      {me?.billing_mode === "slot" && (
        <div className={`rounded-xl border p-3 text-sm font-medium ${blocked ? "bg-red-50 border-red-200 text-red-700" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`} data-testid="enroll-slots-info">
          {blocked ? <><AlertTriangle className="w-4 h-4 inline mr-1" />Slot esauriti: non è possibile effettuare nuove iscrizioni. Contatta l'amministratore per una nuova ricarica.</>
            : <>Slot disponibili: <strong>{me.slots_balance}</strong> · ogni iscrizione confermata scala 1 slot.</>}
        </div>
      )}
      <Tabs defaultValue="single">
        <TabsList>
          <TabsTrigger value="single" data-testid="tab-enroll-single">Iscrizione singola</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-enroll-import">Importazione massiva Excel</TabsTrigger>
        </TabsList>
        <TabsContent value="import"><ImportEnrollments blocked={blocked} onImported={refreshMe} /></TabsContent>
        <TabsContent value="single" className="space-y-6">
      {courses.length === 0 ? (
        <Card className="border-slate-200"><CardContent className="p-6 text-slate-500">Nessun corso disponibile nel tuo catalogo. Contatta l'amministratore.</CardContent></Card>
      ) : (
        <>
          <Card className="border-slate-200"><CardContent className="p-6 space-y-4">
            <div className="font-display font-bold text-lg">1. Selezione corso</div>
            <Select value={courseId} onValueChange={(v) => { setCourseId(v); setEditionId(""); setSpecificId(""); setP((x) => ({ ...x, requires_cfp: false })); }}>
              <SelectTrigger data-testid="enroll-course-select"><SelectValue placeholder="Seleziona un corso" /></SelectTrigger>
              <SelectContent>
                {courses.map((c) => <SelectItem key={c.course_id} value={c.course_id} data-testid={`enroll-course-option-${c.course_id}`}>{isSlot ? c.title : `${c.title} · ${eur(c.price)}`}</SelectItem>)}
              </SelectContent>
            </Select>
            {course && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <ModalityBadge modality={course.modality} />
                  {course.description && <div className="text-sm text-slate-600">{course.description}</div>}
                  {course.duration_hours ? <div className="text-xs text-slate-500">Durata: {course.duration_hours} ore</div> : null}
                  {needsEdition(course.modality) && (
                    <div>
                      <Label className="text-xs">Edizione *</Label>
                      {course.editions.length === 0 ? <div className="text-sm text-red-600" data-testid="enroll-no-editions">Nessuna edizione disponibile al momento.</div> : (
                        <Select value={editionId} onValueChange={setEditionId}>
                          <SelectTrigger data-testid="enroll-edition-select"><SelectValue placeholder="Seleziona edizione" /></SelectTrigger>
                          <SelectContent>
                            {course.editions.map((e) => (
                              <SelectItem key={e.id} value={e.id} disabled={e.seats_available <= 0}>
                                {dateIt(e.start_date)} · {e.location || course.modality} · {e.seats_available > 0 ? `${e.seats_available} posti` : "Completa"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  )}
                </div>
                {!isSlot && <div><div className="font-display font-bold text-lg mb-2">2. Prezzo</div><PriceBox c={course} /></div>}
              </div>
            )}
          </CardContent></Card>
          {course && (
            <Card className="border-slate-200"><CardContent className="p-6 space-y-4">
              <div className="font-display font-bold text-lg">{isSlot ? "2" : "3"}. Dati corsista</div>
              <ParticipantForm value={p} onChange={setP} cfpEnabled={course.cfp_enabled} cfpProfiles={course.cfp_profiles} testPrefix="enroll" />
              {specifics.length > 0 && (
                <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 space-y-2" data-testid="enroll-specific-box">
                  <Label className="text-sm font-semibold text-indigo-900"><Link2 className="w-4 h-4 inline mr-1" />Aggiungi formazione specifica (facoltativo)</Label>
                  <Select value={specificId || "none"} onValueChange={(v) => setSpecificId(v === "none" ? "" : v)}>
                    <SelectTrigger className="bg-white" data-testid="enroll-specific-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nessuna formazione specifica</SelectItem>
                      {specifics.map((s) => <SelectItem key={s.course_id} value={s.course_id}>{isSlot ? s.title : `${s.title} · ${eur(s.price)}`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Il corsista verrà iscritto anche al corso scelto con gli stessi dati{isSlot ? " (scala 1 slot in più)" : ""}.</p>
                </div>
              )}
              <div className="flex justify-end">
                <Button onClick={submit} disabled={busy || blocked} className="h-11 px-6 bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="enroll-submit">
                  {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Conferma iscrizione
                </Button>
              </div>
            </CardContent></Card>
          )}
        </>
      )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
